import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import {
    ArrowDownUp,
    ArrowRightLeft,
    CheckCheck,
    CheckSquare,
    CornerUpRight,
    FileStack,
    HandHelping,
    Inbox,
    LockKeyhole,
    LockKeyholeOpen,
    MinusCircle,
    PlusCircle,
    Square,
    SquarePen,
    Trash2,
    UserRoundX
} from "lucide-react";
import { useMenuContext } from "../../context/useMenuContext";
import { useScheduleContext } from "../../context/useScheduleContext";
import { usePermissions } from "../../usePermissions";
import { useIsTemplate } from "../../useIsTemplate";
import { useRequestCutoff } from "../../useRequestCutoff";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useAccess } from "../../../../../../utils/hooks/access";
import { getResourceUserId } from "../../../helpers/data/groupedResourceId";

export const useEventMenu = ({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openTransferModal,
    openShiftTradeDrawer,
    openMoveToOpenShiftsModal,
}: any) => {
    const { t } = useTranslation();
    const { selected, resource, closeMenu, moreModal } = useMenuContext();
    const [{ mode }] = useFilters();
    const { dispatch, selectedEvents, ctrlShiftS, type } = useScheduleContext();
    const isTeamScheduler = ["team", "template", "profile"].includes(type);
    const { hasAccess: canCreateRequest } = useAccess(!isTeamScheduler && "request.canCreate");
    const { canEdit, canCreate, canCopy, canDelete, canTransfer, canLock, canUnlock, canApprove } = usePermissions();
    const isTemplate = useIsTemplate();
    const { isTooLate: isTooLateForRequest } = useRequestCutoff();

    const isEventSelected = useMemo(() => {
        return selected && !!selectedEvents.find((event: any) => event.id === selected?.id)
    }, [selected, selectedEvents]);

    const onSelectChange = useCallback(() => {
        dispatch({ type: "select-event", payload: { event: selected } })
        if (!ctrlShiftS && ["week", "custom", "month"].includes(mode)) {
            dispatch({ type: "ctrl-shift-s" });
        }
    }, [dispatch, ctrlShiftS, selected, mode]);

    return useMemo(() => {
        const menuItems = [
            {
                icon: isEventSelected ? CheckSquare : Square,
                label: t("select"),
                onChange: onSelectChange,
                hidden: (
                    (!canEdit &&
                        !canCopy &&
                        !canTransfer &&
                        !canLock &&
                        !canUnlock &&
                        !canApprove &&
                        !canDelete
                    ) || moreModal
                ),
                disabled: false,
            },
            {
                icon: PlusCircle,
                label: t("add-another-event"),
                onChange: () => {
                    dispatch({
                        type: "selected-cell-or-event",
                        payload: {
                            date: selected?.date,
                            user: selected?.resource || selected?.user,
                            addAnotherEvent: true,
                            moreModal,
                            isTemplate
                        }
                    });
                    closeMenu();
                },
                hidden: !canCreate || mode === "day",
                disabled: false,
            },
            {
                icon: SquarePen,
                label: canEdit ? t("edit") : t("edit-request"),
                onChange: () => {
                    dispatch({
                        type: "selected-cell-or-event",
                        payload: {
                            ...selected,
                            user: resource || selected?.user,
                            isCreating: false,
                            moreModal,
                            isTemplate
                        }
                    });
                    closeMenu();
                },
                hidden: !canEdit && !canCreateRequest,
                disabled: selected?.locked,
            },
            {
                icon: FileStack,
                label: t("copy"),
                onChange: () => {
                    openCopyModal({
                        event: selected?.id,
                        user: selected?.user,
                        type: "cell",
                        isTemplate
                    });
                    closeMenu();
                },
                hidden: !canCopy,
                disabled: false,
            },
            {
                icon: CheckCheck,
                label: t("submit"),
                onChange: () => {
                    openActionsModal({
                        eventId: selected.id,
                        userId: getResourceUserId(selected?.resourceId),
                        type: "cell",
                        action: "submit"
                    });
                    closeMenu();
                },
                hidden: !canCreate || isTeamScheduler || isTemplate,
                disabled: selected?.locked || ["approved", "submitted"].includes(selected?.status)
            },
            {
                icon: ArrowDownUp,
                label: t("transfer"),
                onChange: () => {
                    openTransferModal({
                        type: "cell",
                        eventId: selected?.id,
                        userId: selected?.user,
                        moreModal,
                        isTemplate
                    });
                    closeMenu();
                },
                hidden: !canTransfer,
                disabled: selected?.locked,
            },
            {
                icon: LockKeyhole,
                label: `${t("lock")} ${selectedEvents?.length > 1 ? `(${selectedEvents?.length})` : ""}`,
                onChange: () => {
                    const data = selectedEvents?.length > 1 ? {
                        ids: selectedEvents?.map(({ id }: any) => id),
                        action: "lock",
                        type: "bulk",
                        moreModal
                    } : {
                        type: "cell",
                        eventId: selected?.id,
                        userId: getResourceUserId(selected?.resourceId),
                        action: "lock",
                        moreModal
                    };
                    openActionsModal(data);
                    closeMenu();
                },
                hidden: isTemplate || (selected?.locked &&
                    (selectedEvents?.length === 1 || !selectedEvents?.length)) || !canLock || !isTeamScheduler,
                // disabled: selected?.locked,
            },
            {
                icon: LockKeyholeOpen,
                label: `${t("unlock")} ${selectedEvents?.length > 1 ? `(${selectedEvents?.length})` : ""}`,
                onChange: () => {
                    const data = selectedEvents?.length > 1 ? {
                        ids: selectedEvents.map(({ id }: any) => id),
                        action: "unlock",
                        type: "bulk",
                        moreModal
                    } : {
                        type: "cell",
                        eventId: selected?.id,
                        userId: getResourceUserId(selected?.resourceId),
                        action: "unlock",
                        moreModal
                    };
                    openActionsModal(data);
                    closeMenu();
                },
                hidden: (!selected?.locked &&
                    (selectedEvents?.length === 1 || !selectedEvents?.length)) ||
                    !canUnlock ||
                    !isTeamScheduler ||
                    isTemplate,
                // disabled: !selected?.locked,
            },
            {
                icon: CornerUpRight,
                label: `${t("publish")} ${selectedEvents?.length > 1 ? `(${selectedEvents?.length})` : ""}`,
                onChange: () => {
                    const data = selectedEvents?.length > 1 ? {
                        ids: selectedEvents.map(({ id }: any) => id),
                        action: "publish",
                        type: "bulk",
                        moreModal
                    } : {
                        type: "cell",
                        eventId: selected?.id,
                        userId: getResourceUserId(selected?.resourceId),
                        action: "publish",
                        moreModal
                    };
                    openActionsModal(data);
                    closeMenu();
                },
                hidden: isTemplate || !canApprove || (selected?.status === "approved" &&
                    (selectedEvents?.length === 1 || !selectedEvents?.length)) || !isTeamScheduler,
                disabled: (selected?.locked && (selectedEvents?.length === 1 || !selectedEvents?.length)) || selected?.status === "approved",
            },
            {
                icon: MinusCircle,
                label: `${t("unpublish")} ${selectedEvents?.length > 1 ? `(${selectedEvents?.length})` : ""}`,
                onChange: () => {
                    const data = selectedEvents?.length > 1 ? {
                        ids: selectedEvents.map(({ id }: any) => id),
                        action: "unpublish",
                        type: "bulk",
                        moreModal
                    } : {
                        type: "cell",
                        eventId: selected?.id,
                        userId: getResourceUserId(selected?.resourceId),
                        action: "unpublish",
                        moreModal
                    };
                    openActionsModal(data);
                    closeMenu();
                },
                hidden: isTemplate || !canApprove || (selected?.status === "pending" &&
                    (selectedEvents?.length === 1 || !selectedEvents?.length)) || !isTeamScheduler,
                disabled: (selected?.locked && (selectedEvents?.length === 1 || !selectedEvents?.length)) || selected?.status === "pending",
            },
            {
                icon: ArrowRightLeft,
                label: t("swap-shift"),
                onChange: () => {
                    openShiftTradeDrawer({
                        type: "swap",
                        event: selected
                    });
                    closeMenu();
                },
                hidden: isTemplate || isTeamScheduler || (selected?.status !== "approved" && !isTeamScheduler),
                disabled: moment(selected?.startDate || selected?.date).isBefore(moment(), "seconds")
            },
            {
                icon: HandHelping,
                label: t("offer-shift"),
                onChange: () => {
                    openShiftTradeDrawer({
                        type: "offer",
                        event: selected
                    });
                    closeMenu();
                },
                hidden: isTemplate || isTeamScheduler || (selected?.status !== "approved" && !isTeamScheduler),
                disabled: moment(selected?.startDate || selected?.date).isBefore(moment(), "seconds")
            },
            {
                icon: Inbox,
                label: t("move-to-open-shifts"),
                onChange: () => {
                    openMoveToOpenShiftsModal(selected);
                    closeMenu();
                },
                hidden: isTemplate,//|| isTeamScheduler,
                disabled: isTooLateForRequest(selected?.startDate || selected?.date) || selected?.locked || selected?.status !== "approved",
            },
            {
                icon: UserRoundX,
                label: t("call-off"),
                onChange: () => {
                    openMoveToOpenShiftsModal({ ...selected, requestType: "call-off" });
                    closeMenu();
                },
                hidden: isTemplate, // || !canApprove,
                disabled: selected?.locked || isTooLateForRequest(selected?.startDate || selected?.date) || selected?.status !== "approved",
            },
            {
                icon: Trash2,
                label: `${t("delete")} ${selectedEvents?.length > 1 ? `(${selectedEvents?.length})` : ""}`,
                onChange: () => {
                    const data = selectedEvents?.length > 1 ? {
                        type: "bulk",
                        ids: selectedEvents?.map(({ id }: { id: string }) => id),
                        moreModal,
                        isTemplate
                    } : {
                        type: "cell",
                        event: { ...selected, isTemplate },
                        moreModal
                    };
                    openDeleteModal(data);
                    closeMenu();
                },
                hidden: !canDelete,
                disabled: selected?.locked,
            },
        ];
        return menuItems.filter((item: any) => !item.hidden);
    }, [
        isTemplate,
        resource,
        moreModal,
        isTeamScheduler,
        selectedEvents,
        isEventSelected,
        selected,
        mode,
        canEdit,
        canCopy,
        canTransfer,
        canLock,
        canUnlock,
        canApprove,
        canDelete,
        canCreate,
        canCreateRequest,
        isTooLateForRequest,
        dispatch,
        onSelectChange,
        closeMenu,
        openShiftTradeDrawer,
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openTransferModal,
        openMoveToOpenShiftsModal,
        t
    ]);
}
